// @ts-strict-ignore
import type {
  Action,
  Threshold,
  DataRoute,
  Attribute,
  List,
  Outcome,
  PublishedAttribute,
  ReasonCode,
  ReasonCodeGroup,
  ServiceObject,
  ServicesByName,
  Tag,
} from 'flow/store/engine/types';
import { createSnippetVersion, getIncomerActionsForAction } from '../../snippets';
import {
  actionHasRules,
  convertActionOptionSelectedValueIdsToSlugs,
  findHighestExistingActionPrecedence,
  getActionName,
  getActionPrecedenceIndex,
  getNonPrecedenceIndexedActions,
  getPrecedenceIndexedActions,
  getPublishedDetails,
  indexActionsByPublicId,
  isActionOfType,
  isDefaultPrecedenceIndexedAction,
  setActionName,
  setActionPrecedenceIndex,
  sortActionsByPrecedenceIndex,
} from 'flow/service/actions';
import {
  buildRuleTree,
  hasMultipleThresholds,
  toggleGroupTypeInRuleTree,
  insertRuleIntoRuleTree,
  getChildRule,
  groupType,
  buildGroupRule,
  groupRulesInRuleTree,
} from 'flow/service/rules';
import { isListAction } from 'flow/service/actions/services/lists';
import { indexDataRoutesByPublicId } from 'flow/service/dataRoutes';
import { indexListsByName, isListEphemeral, generateTemporaryListToken } from 'flow/service/lists';
import { indexOutcomesByName } from 'flow/service/outcomes';
import {
  isPublishedAttribute,
  indexPublishedAttributesByName,
  generateTemporaryPublishedAttributeToken,
} from 'flow/service/publishedAttributes';
import {
  indexReasonCodeGroupsByName,
  indexReasonCodesByServiceCodeKey,
  getHighestReasonCodeGroupId,
  createReasonCodeServiceCodeKey,
} from 'flow/service/reasonCodes';
import {
  createEphemeralAttributeIdAssigner,
  getAttributesForServices,
  indexAttributesByEquivalencyKey,
  getHighestAttributeId,
  indexServiceAttributesById,
  indexServiceInternalNamesByAttributeId,
  indexServicesByInternalName,
  indexServiceConfigPossibleValuesBySlug,
} from 'flow/service/services';
import { CUSTOMER_LIST_ID_OPTION_KEY } from 'flow/service/services/constants';
import { indexTagsByName } from 'flow/service/tags';
import { indexThresholdsByPublicId } from 'flow/service/thresholds';
import { hasNameCollision, computeNextName } from 'flow/service/util';
import { ActionType } from 'flow/store/engine/constants';
import { getCollisionsWithinPrimitives, buildPrimitives } from '../../collisions';
import {
  getEquivalencyKeyForPrimitive,
  indexPrimitiveEquivalencyKeysByIdentifier,
  indexServiceConfigOptionsByEquivalencyKey,
} from '../../collisions/equivalency';
import type { EquivalencyKey } from '../../collisions/equivalency';
import type { SnippetVersion } from '../../types';
import { extractIdentifiersFromServiceActionOption, getEquivalencyKeyForServiceConfigOption } from '../../collisions/equivalency/equivalency';
import type { ServiceConfigOption } from '~/dashboard/views/settings/service-configuration/types';


export function isValidActionMerge(args: {
  snippet: {
    actionToMerge: Action;
    actions: Record<Action['publicId'], Action>;
    thresholds: Record<Threshold['publicId'], Threshold>;
    dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  };
  workflow: {
    actionToMergeInto: Action;
    actions: Record<Action['publicId'], Action>;
    thresholds: Record<Threshold['publicId'], Threshold>;
    dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  };
  actionMergeMap: Map<Action['publicId'], Action['publicId']>;
}): boolean {
  if (!isInsertionTopologicallyValid(args)) {
    return false;
  }

  return true;
}


/**
 * Evaluates whether merging an action into a workflow causes a cycle.
 * Cycles are not supported, as our graph is strictly a Direct Acyclical Graph (DAG)
 */
function isInsertionTopologicallyValid(args: {
  snippet: {
    actionToMerge: Action;
    actions: Record<Action['publicId'], Action>;
    thresholds: Record<Threshold['publicId'], Threshold>;
    dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  };
  workflow: {
    actionToMergeInto: Action;
    actions: Record<Action['publicId'], Action>;
    thresholds: Record<Threshold['publicId'], Threshold>;
    dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  };
  actionMergeMap: Map<Action['publicId'], Action['publicId']>;
}): boolean {
  /**
   * The traversal algorithm is essentially breadth-first search on all action incomers,
   * with the extra complexity of action merging. To account for merging, we need
   * to look up incomers in both the snippet and the workflow graphs.
   * Steps:
   * 1. Grab all incomer actions for the snippet action in question, both from the snippet and wf.
   * 2. Evaluate to see if a incomer has been merged, if so, replace the incomer Action
   *    with the merged one from the workflow.
   * 3. Keep all incomer ids, and see if the target merge node (where we are trying to merge into)
   *    is part of the incomers. If it is, a cycle would be created, so `false` is returned.
   * 4. Add all incomers to the queue and continue evaluating with BFS. This will include
   *    previously merged incomers, if any.
   * 5. If the merge target is never found in the incomer graph, the merge is valid
   */
  const { actionMergeMap, snippet, workflow } = args;

  const queue: Action[] = [snippet.actionToMerge];
  const ancestorActionIds = new Set<Action['publicId']>();

  // Breadth-first search on all nodes connected to the sourceAction
  for (const action of queue) {
    const incomerSnippetActions = getIncomerActionsForAction({
      action,
      actions: snippet.actions,
      thresholds: snippet.thresholds,
      dataRoutes: snippet.dataRoutes,
    });

    const incomerWorkflowActions = getIncomerActionsForAction({
      action,
      actions: workflow.actions,
      thresholds: workflow.thresholds,
      dataRoutes: workflow.dataRoutes,
    });

    const allIncomerActionsForAction = new Map<Action['publicId'], Action>([
      ...Object.entries(incomerSnippetActions),
      ...Object.entries(incomerWorkflowActions),
    ]);


    // look for merged nodes in all incomers and swap them for the merged one
    for (const [mergedSnippetActionId, mergedWorkflowActionId] of actionMergeMap) {
      if (allIncomerActionsForAction.has(mergedSnippetActionId)) {
        allIncomerActionsForAction.delete(mergedSnippetActionId);

        const mergedWorkflowAction = workflow.actions[mergedWorkflowActionId];
        allIncomerActionsForAction.set(mergedWorkflowActionId, mergedWorkflowAction);
      }
    }

    const visitedActions = [...allIncomerActionsForAction.values()];

    for (const visitedAction of visitedActions) {
      ancestorActionIds.add(visitedAction.publicId);
    }


    // if the merge target is part of the ancestry, a cycle would be created
    if (ancestorActionIds.has(workflow.actionToMergeInto.publicId)) {
      return false;
    }
    queue.push(...visitedActions);
  }


  return true;
}


/**
 * Merges the rules of the snippetAction into the workflowAction.
 * For now, this is only used in the context of snippets, but it will likely be moved to a
 * separate service
 */
export function mergeActionRules(args: {
  snippetAction: Action;
  workflowAction: Action;
}) {
  const { snippetAction, workflowAction } = args;

  // built rules are guaranteed to be a Group
  const snippetActionRuleTree = buildRuleTree(snippetAction.rules);

  if (!actionHasRules(workflowAction)) {
    return buildRuleTree(snippetAction.rules);
  }

  const workflowActionRuleTree = buildRuleTree(workflowAction.rules);

  const snippetActionHasSingleThresholdRule = (
    !hasMultipleThresholds(snippetActionRuleTree)
  );
  const workflowActionHasSingleThresholdRule = (
    !hasMultipleThresholds(workflowActionRuleTree)
  );

  // simple case - both rules are single-threshold. only need to append a threshold under an 'or'
  if (snippetActionHasSingleThresholdRule
    && workflowActionHasSingleThresholdRule) {
    /*
     * by default, single-threshold rules are contained in an 'and'. since we always want an 'or',
     * we can safely toggle the group
     */
    const toggledRule = toggleGroupTypeInRuleTree({
      treeRoot: workflowActionRuleTree,
      rule: workflowActionRuleTree,
    });

    // merge the first (only) threshold rule of the snippet 'after' the first (only) rule of the group
    const mergedRule = insertRuleIntoRuleTree(
      toggledRule,
      {
        position: 'after',
        relativelyPositionedRule: getChildRule(toggledRule, 0),
        ruleToInsert: getChildRule(snippetActionRuleTree, 0),
      },
    );

    return mergedRule;
  }

  // top-level merge groups should be 'or'
  let toggledRuleTree = workflowActionRuleTree;
  if (workflowActionRuleTree.data.groupType === groupType.and) {
    toggledRuleTree = toggleGroupTypeInRuleTree({
      rule: workflowActionRuleTree,
      treeRoot: workflowActionRuleTree,
    });
  }

  const groupToAdd = buildGroupRule({
    groupType: workflowActionHasSingleThresholdRule
      ? groupType.and
      : workflowActionRuleTree.data.groupType,
  });

  // group whatever the workflow action has in a new group
  const toggledGroupedRuleTree = groupRulesInRuleTree(
    toggledRuleTree,
    {
      groupToInsert: groupToAdd,
      parentRule: toggledRuleTree,
      indices: {
        start: 0,
        end: toggledRuleTree.data.children.length,
      },
    },
  );

  // add the snippet rules as a sibling of the now-grouped workflow action rules
  const mergedRuleTree = insertRuleIntoRuleTree(
    toggledGroupedRuleTree,
    {
      position: 'after',
      relativelyPositionedRule: groupToAdd,
      ruleToInsert: snippetActionRuleTree,
    },
  );
  return mergedRuleTree;
}


/**
 * Returns a copy of the snippet with updated account primitives and their corresponding actions.
 * In essence, it runs the snippet through a pipeline with the following operations:
 *  - rename all props and actions that depend on them to resolve any collisions with the account
 *  - flag all ephemeral props and their corresponding actions
 *  - remap service attributes and reason codes to the account
 *  - remaps published attributes
 *  - sorts the incoming outcomes and wf router indices to be incrementally larger than the workflow's
 *
 * For example:
 * If a snippet has a tag prop & tag action with name 'Approved' and the account
 * contains a tag with the same name but different configuration (no equivalency collision),
 * the tag in the snippet is renamed, and any snippet tag actions that point to it receive the
 * updated name.
 *
 * If a snippet has an outcome prop of "Suspended" and it does not exist in the account, the
 * outcome prop & action are flagged as ephemeral.
 */
export function contextualizeSnippetForTargetWorkflow(args: {
  snippet: SnippetVersion;
  workflowPrimitives: {
    actions: Record<Action['publicId'], Action>;
  };
  accountPrimitives: {
    tags: Tag[];
    outcomes: Outcome[];
    services: ServicesByName;
    publishedAttributes: PublishedAttribute[];
    reasonCodeGroups: ReasonCodeGroup[];
    reasonCodes: ReasonCode[];
    lists: List[];
    globalServiceConfigOptions: ServiceConfigOption[];
  };
}): SnippetVersion {
  const { snippet, accountPrimitives, workflowPrimitives } = args;

  const collisionsWithinAccountPrimitives = getCollisionsWithinPrimitives({
    primitives: buildPrimitives({
      actions: workflowPrimitives.actions,
      services: accountPrimitives.services,
      tags: accountPrimitives.tags,
      outcomes: accountPrimitives.outcomes,
      publishedAttributes: accountPrimitives.publishedAttributes,
      reasonCodeGroups: accountPrimitives.reasonCodeGroups,
      lists: accountPrimitives.lists,
      globalServiceConfigOptions: accountPrimitives.globalServiceConfigOptions,
    }),
    context: {
      workflowData: {
        services: accountPrimitives.services,
        globalServiceConfigOptions: accountPrimitives.globalServiceConfigOptions,
      },
    },
  });


  // Snippet with renamed primitive props to resolve non-equivalent name collisions
  const snippetWithRenamedPrimitives = resolveNameCollisions({
    accountCollisionMap: collisionsWithinAccountPrimitives,
    accountPrimitives: {
      tags: accountPrimitives.tags,
      outcomes: accountPrimitives.outcomes,
      publishedAttributes: accountPrimitives.publishedAttributes,
      reasonCodeGroups: accountPrimitives.reasonCodeGroups,
      lists: accountPrimitives.lists,
    },
    snippet,
  });


  // Snippet with ephemeral tag & outcome props, as well as tag & outcome actions
  const snippetWithFlaggedEphemeralPrimitives = flagEphemeralPrimitives({
    accountCollisionMap: collisionsWithinAccountPrimitives,
    snippet: snippetWithRenamedPrimitives,
  });


  /**
   * Snippet with updated thresholds & data routes to use the service attributes from the target
   * account's environment
  */
  const snippetWithResolvedServiceAttributes = resolveServiceAttributes({
    snippet: snippetWithFlaggedEphemeralPrimitives,
    accountCollisionMap: collisionsWithinAccountPrimitives,
    accountPrimitives: {
      services: Object.values(accountPrimitives.services),
    },
  });
  // Snippet with updated service config option ids from the target acct's environment
  const snippetWithResolvedServiceConfigOptionIds = resolveServiceConfigOptionIds({
    snippet: snippetWithResolvedServiceAttributes,
    accountCollisionMap: collisionsWithinAccountPrimitives,
    accountPrimitives: {
      services: accountPrimitives.services,
      globalServiceConfigOptions: accountPrimitives.globalServiceConfigOptions,
    },
  });
  // Snippet with updated published attribute tokens in both props and actions
  const snippetWithResolvedPublishedAttributes = resolvePublishedAttributes({
    snippet: snippetWithResolvedServiceConfigOptionIds,
    accountCollisionMap: collisionsWithinAccountPrimitives,
  });


  // Snippet with RCGs from the account instead of the snippets, if they have a collision
  const snippetWithResolvedReasonCodeGroups = resolveReasonCodeGroups({
    snippet: snippetWithResolvedPublishedAttributes,
    accountCollisionMap: collisionsWithinAccountPrimitives,
    accountPrimitives: {
      reasonCodeGroups: accountPrimitives.reasonCodeGroups,
      reasonCodes: accountPrimitives.reasonCodes,
    },
  });


  // Snippet with the correct precedence indexes for indexed actions (outcomes and workflow routers)
  const snippetWithResolvedPrecedenceIndexedActions = resolvePrecedenceIndexedActions({
    snippet: snippetWithResolvedReasonCodeGroups,
    accountCollisionMap: collisionsWithinAccountPrimitives,
    workflowPrimitives: {
      actions: workflowPrimitives.actions,
    },
  });

  const snippetWithResolvedLists = resolveLists({
    snippet: snippetWithResolvedPrecedenceIndexedActions,
    accountCollisionMap: collisionsWithinAccountPrimitives,
  });


  return snippetWithResolvedLists;
}


/**
 * Builds a Map containing the name of a prop and the name it should be renamed to.
 * @example
 *  ["tag-a", "tag-a (1)"]
 *  ["approved", "approved (1)"]
 *  ["denied (1)", "denied (2)"]
 */
function buildPropRenameMap<
  P extends Outcome | Tag | PublishedAttribute | ReasonCodeGroup | List,
>(args: {
  accountProps: P[];
  snippetProps: P[];
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
  // function to extract the name from the prop (changes depending on what prop P is passed)
  nameExtractor: (primitive: P) => string;
}): Map<string, string> {
  const {
    accountProps, snippetProps, accountCollisionMap, nameExtractor,
  } = args;


  /**
   * it's possible that the next name of a snippet prop collides with the name of another
   * snippet prop. for example, if the snippet contains tags: 'test' and 'test (1)'. the next
   * name for 'test' should be 'test (2)', as 'test (1' is already taken by another snippet prop.
   * hence, we need to check in both the account and snippet props.
  */
  const uniqueAccountPropNames = new Set(accountProps.map(nameExtractor));
  const uniqueSnippetPropNames = new Set(snippetProps.map(nameExtractor));
  const uniqueSnippetAndAccountPropNames = new Set([
    ...uniqueAccountPropNames,
    ...uniqueSnippetPropNames,
  ]);

  const snippetPropsWithoutAccountEquivalent = snippetProps.filter(snippetProp => {
    const snippetPropEquivalencyKey = getEquivalencyKeyForPrimitive({
      primitive: snippetProp,
    });
    return !accountCollisionMap.has(snippetPropEquivalencyKey);
  });

  // no equivalency, but the names happen to be the same
  const snippetPropsWithAccountNameCollisions = (
    snippetPropsWithoutAccountEquivalent.filter(snippetProp => {
      const snippetPropName = nameExtractor(snippetProp);
      return hasNameCollision(snippetPropName, uniqueAccountPropNames);
    })
  );


  const nextPropNameMap = new Map<string, string>();
  snippetPropsWithAccountNameCollisions.forEach(snippetProp => {
    const currentSnippetPropName = nameExtractor(snippetProp);
    const nextSnippetPropName = (
      computeNextName(currentSnippetPropName, uniqueSnippetAndAccountPropNames)
    );

    nextPropNameMap.set(currentSnippetPropName, nextSnippetPropName);
  });


  return nextPropNameMap;
}


/**
 * Returns a copy of a snippet with renamed primitives to resolve collisions with
 * the account.
 */
function resolveNameCollisions(args: {
  snippet: SnippetVersion;
  accountPrimitives: {
    tags: Tag[];
    outcomes: Outcome[];
    publishedAttributes: PublishedAttribute[];
    reasonCodeGroups: ReasonCodeGroup[];
    lists: List[];
  };
  accountCollisionMap: Map<string, Set<string>>;
}): SnippetVersion {
  const {
    snippet, accountCollisionMap, accountPrimitives,
  } = args;

  // Map containing all snippet tag props that need to be renamed, along with their new names
  const tagRenameMap = buildPropRenameMap({
    accountCollisionMap,
    accountProps: accountPrimitives.tags,
    nameExtractor: tag => tag.tag_name,
    snippetProps: snippet.props.tags,
  });


  // Map containing all snippet outcome props that need to be renamed, along with their new names
  const outcomeRenameMap = buildPropRenameMap({
    accountCollisionMap,
    accountProps: accountPrimitives.outcomes,
    nameExtractor: outcome => outcome.outcome,
    snippetProps: snippet.props.outcomes,
  });


  // Map containing all snippet published attribute props that need to be renamed, along with their new names
  const publishedAttributeRenameMap = buildPropRenameMap({
    accountCollisionMap,
    accountProps: accountPrimitives.publishedAttributes,
    nameExtractor: publishedAttribute => publishedAttribute.attributeName,
    snippetProps: snippet.props.publishedAttributes,
  });


  // Map containing all snippet reason code group props that need to be renamed, along with their new names
  const reasonCodeGroupRenameMap = buildPropRenameMap({
    accountCollisionMap,
    accountProps: accountPrimitives.reasonCodeGroups,
    nameExtractor: reasonCodeGroup => reasonCodeGroup.name,
    snippetProps: snippet.props.reasonCodeGroups,
  });

  const listsRenameMap = buildPropRenameMap({
    accountCollisionMap,
    accountProps: accountPrimitives.lists,
    nameExtractor: list => list.list_name,
    snippetProps: snippet.props.lists,
  });

  const renamedSnippetTagProps = snippet.props.tags.map(snippetTagProp => {
    const currentSnippetTagPropName = snippetTagProp.tag_name;

    if (!tagRenameMap.has(currentSnippetTagPropName)) {
      return snippetTagProp;
    }
    return {
      ...snippetTagProp,
      tag_name: tagRenameMap.get(currentSnippetTagPropName),
    };
  });

  const renamedSnippetOutcomeProps = snippet.props.outcomes.map(snippetOutcomeProp => {
    const currentSnippetOutcomePropName = snippetOutcomeProp.outcome;

    if (!outcomeRenameMap.has(currentSnippetOutcomePropName)) {
      return snippetOutcomeProp;
    }
    return {
      ...snippetOutcomeProp,
      outcome: outcomeRenameMap.get(currentSnippetOutcomePropName),
    };
  });

  const renamedPublishedAttributes = snippet
    .props.publishedAttributes.map(snippetPublishedAttribute => {
      const currentPublishedAttributeName = snippetPublishedAttribute.attributeName;
      if (!publishedAttributeRenameMap.has(currentPublishedAttributeName)) {
        return snippetPublishedAttribute;
      }

      return {
        ...snippetPublishedAttribute,
        attributeName: publishedAttributeRenameMap.get(currentPublishedAttributeName),
      };
    });

  const renamedSnippetReasonCodeGroups = snippet
    .props.reasonCodeGroups.map(snippetReasonCodeGroup => {
      const currentSnippetReasonCodeGroupName = snippetReasonCodeGroup.name;
      if (!reasonCodeGroupRenameMap.has(currentSnippetReasonCodeGroupName)) {
        return snippetReasonCodeGroup;
      }
      return {
        ...snippetReasonCodeGroup,
        name: reasonCodeGroupRenameMap.get(currentSnippetReasonCodeGroupName),
      };
    });

  const renamedSnippetListProps = snippet.props.lists.map(snippetList => {
    const currentSnippetListName = snippetList.list_name;
    if (!listsRenameMap.has(currentSnippetListName)) {
      return snippetList;
    }
    return {
      ...snippetList,
      list_name: listsRenameMap.get(currentSnippetListName),
    };
  });

  const renamedSnippetActions = Object.values(snippet.data.actions).map(snippetAction => {
    if (isActionOfType(snippetAction, ActionType.TAG)) {
      const currentSnippetTagActionName = getActionName(snippetAction);
      if (tagRenameMap.has(currentSnippetTagActionName)) {
        const nextTagPropName = tagRenameMap.get(currentSnippetTagActionName);
        return setActionName(snippetAction, nextTagPropName);
      }
    }

    if (isActionOfType(snippetAction, ActionType.OUTCOME)) {
      const currentSnippetOutcomeActionName = getActionName(snippetAction);
      if (outcomeRenameMap.has(currentSnippetOutcomeActionName)) {
        const nextOutcomePropName = outcomeRenameMap.get(currentSnippetOutcomeActionName);
        return setActionName(snippetAction, nextOutcomePropName);
      }
    }


    // handles INPUT and OUTPUT actions
    if (isPublishedAttribute(snippetAction)) {
      const currentSnippetAttributeActionName = getPublishedDetails(snippetAction)?.attributeName;
      if (publishedAttributeRenameMap.has(currentSnippetAttributeActionName)) {
        const nextPublishedAttributePropName = (
          publishedAttributeRenameMap.get(currentSnippetAttributeActionName)
        );
        return setActionName(snippetAction, nextPublishedAttributePropName);
      }
    }

    if (isListAction(snippetAction)) {
      const currentSnippetListActionName = getActionName(snippetAction);
      if (listsRenameMap.has(currentSnippetListActionName)) {
        const nextListPropName = listsRenameMap.get(currentSnippetListActionName);
        return setActionName(snippetAction, nextListPropName);
      }
    }
    return snippetAction;
  });

  const renamedSnippetThresholds = Object.values(snippet.data.thresholds).map(snippetThreshold => {
    if (snippetThreshold.right?.reasonCodeGroup) {
      const currentSnippetRCGName = snippetThreshold.right.reasonCodeGroup.name;

      if (reasonCodeGroupRenameMap.has(currentSnippetRCGName)) {
        const nextRCGName = reasonCodeGroupRenameMap.get(currentSnippetRCGName);
        return {
          ...snippetThreshold,
          right: {
            ...snippetThreshold.right,
            reasonCodeGroup: {
              ...snippetThreshold.right.reasonCodeGroup,
              name: nextRCGName,
            },
          },
        };
      }
    }

    return snippetThreshold;
  });


  return createSnippetVersion({
    actions: indexActionsByPublicId(renamedSnippetActions),
    dataRoutes: snippet.data.dataRoutes,
    thresholds: indexThresholdsByPublicId(renamedSnippetThresholds),
    outcomes: renamedSnippetOutcomeProps,
    publishedAttributes: renamedPublishedAttributes,
    reasonCodeGroups: renamedSnippetReasonCodeGroups,
    services: snippet.props.services,
    tags: renamedSnippetTagProps,
    lists: renamedSnippetListProps,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions ?? [],
    metadata: snippet.metadata,
  });
}


/**
 * Returns a copy of a Snippet with primitives marked as ephemeral if they
 * do not exist in the account
 */
function flagEphemeralPrimitives(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<string, Set<string>>;
}): SnippetVersion {
  const { snippet, accountCollisionMap } = args;

  const flaggedTagProps = snippet.props.tags.map(snippetTagProp => {
    const snippetTagPropEquivalencyKey = getEquivalencyKeyForPrimitive({
      primitive: snippetTagProp,
    });

    return {
      ...snippetTagProp,
      is_ephemeral: !accountCollisionMap.has(snippetTagPropEquivalencyKey),
    };
  });

  const flaggedOutcomeProps = snippet.props.outcomes.map(snippetOutcomeProp => {
    const snippetOutcomePropEquivalencyKey = getEquivalencyKeyForPrimitive({
      primitive: snippetOutcomeProp,
    });
    return {
      ...snippetOutcomeProp,
      is_ephemeral: !accountCollisionMap.has(snippetOutcomePropEquivalencyKey),
    };
  });

  const flaggedPublishedAttributeProps = snippet.props.publishedAttributes
    .map(snippetPublishedAttribute => {
      const snippetPublishedAttributeEquivalencyKey = getEquivalencyKeyForPrimitive({
        primitive: snippetPublishedAttribute,
      });

      return {
        ...snippetPublishedAttribute,
        isEphemeral: !accountCollisionMap.has(snippetPublishedAttributeEquivalencyKey),
      };
    });

  const flaggedReasonCodeGroupProps = snippet.props.reasonCodeGroups.map(snippetRCG => {
    const snippetRCGEquivalencyKey = getEquivalencyKeyForPrimitive({
      primitive: snippetRCG,
    });

    return {
      ...snippetRCG,
      isEphemeral: !accountCollisionMap.has(snippetRCGEquivalencyKey),
    };
  });

  const flaggedListProps = snippet.props.lists.map(snippetList => {
    const snippetListEquivalencyKey = getEquivalencyKeyForPrimitive({
      primitive: snippetList,
    });

    return {
      ...snippetList,
      is_ephemeral: !accountCollisionMap.has(snippetListEquivalencyKey),
    };
  });

  // Flag attributes within services as ephemeral
  const servicesWithFlaggedAttributes = snippet.props.services.map(service => {
    const flaggedAttributes = Object.fromEntries(
      Object.entries(service.attributes ?? {}).map(([attrId, attribute]) => {
        const attributeEquivalencyKey = getEquivalencyKeyForPrimitive({
          primitive: {
            attribute,
            serviceInternalName: service.internal_name,
          },
        });
        return [
          attrId,
          {
            ...attribute,
            isEphemeral: !accountCollisionMap.has(attributeEquivalencyKey),
          },
        ];
      }),
    );

    return {
      ...service,
      attributes: flaggedAttributes,
    };
  });

  const flaggedSnippetTagsByName = indexTagsByName(flaggedTagProps);
  const flaggedSnippetOutcomesByName = indexOutcomesByName(flaggedOutcomeProps);
  const flaggedSnippetPAsByName = indexPublishedAttributesByName(flaggedPublishedAttributeProps);
  const flaggedSnippetRCGsByName = indexReasonCodeGroupsByName(flaggedReasonCodeGroupProps);
  const flaggedSnippetListsByName = indexListsByName(flaggedListProps);

  const flaggedActions = Object.values(snippet.data.actions).map(snippetAction => {
    if (isActionOfType(snippetAction, ActionType.TAG)) {
      const snippetTagActionName = getActionName(snippetAction);
      const snippetTagPropForAction = flaggedSnippetTagsByName[snippetTagActionName];
      return {
        ...snippetAction,
        tag: {
          ...snippetAction.tag,
          isEphemeral: Boolean(snippetTagPropForAction.is_ephemeral),
        },
      };
    }
    if (isActionOfType(snippetAction, ActionType.OUTCOME)) {
      const snippetOutcomeActionName = getActionName(snippetAction);
      const snippetOutcomePropForAction = flaggedSnippetOutcomesByName[snippetOutcomeActionName];
      return {
        ...snippetAction,
        outcome: {
          ...snippetAction.outcome,
          isEphemeral: Boolean(snippetOutcomePropForAction.is_ephemeral),
        },
      };
    }

    if (isPublishedAttribute(snippetAction)) {
      const snippetAttributeActionName = getPublishedDetails(snippetAction)?.attributeName;

      const snippetPublishedAttributePropForAction = (
        flaggedSnippetPAsByName[snippetAttributeActionName]
      );

      if (isActionOfType(snippetAction, ActionType.INPUT)) {
        return {
          ...snippetAction,
          input: {
            ...snippetAction.input,
            publishedDetails: {
              ...snippetAction.input.publishedDetails,
              isEphemeral: Boolean(snippetPublishedAttributePropForAction?.isEphemeral),
            },
          },
        };
      }

      if (isActionOfType(snippetAction, ActionType.OUTPUT)) {
        return {
          ...snippetAction,
          output: {
            ...snippetAction.output,
            publishedDetails: {
              ...snippetAction.output.publishedDetails,
              isEphemeral: Boolean(snippetPublishedAttributePropForAction?.isEphemeral),
            },
          },
        };
      }
    }

    if (isListAction(snippetAction)) {
      const snippetListActionName = getActionName(snippetAction);
      const snippetListPropForAction = flaggedSnippetListsByName[snippetListActionName];
      return {
        ...snippetAction,
        service: {
          ...snippetAction.service,
          isListEphemeral: isListEphemeral(snippetListPropForAction),
        },
      };
    }

    return snippetAction;
  });

  const flaggedThresholds = Object.values(snippet.data.thresholds).map(snippetThreshold => {
    const thresholdRCG = snippetThreshold.right?.reasonCodeGroup;
    if (thresholdRCG) {
      const snippetRCGPropForThreshold = flaggedSnippetRCGsByName[thresholdRCG.name];

      if (snippetRCGPropForThreshold) {
        return {
          ...snippetThreshold,
          right: {
            ...snippetThreshold.right,
            reasonCodeGroup: {
              ...snippetThreshold.right?.reasonCodeGroup,
              isEphemeral: Boolean(snippetRCGPropForThreshold.isEphemeral),
            },
          },
        };
      }
    }

    return snippetThreshold;
  });


  return createSnippetVersion({
    actions: indexActionsByPublicId(flaggedActions),
    dataRoutes: snippet.data.dataRoutes,
    thresholds: indexThresholdsByPublicId(flaggedThresholds),
    outcomes: flaggedOutcomeProps,
    services: servicesWithFlaggedAttributes,
    tags: flaggedTagProps,
    publishedAttributes: flaggedPublishedAttributeProps,
    reasonCodeGroups: flaggedReasonCodeGroupProps,
    lists: flaggedListProps,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions ?? [],
    metadata: snippet.metadata,
  });
}


/**
 * Resolves an attribute to its final form: for attributes that collide with
 * account-level attributes, returns the account's canonical attribute;
 * otherwise returns the attribute with an existing or newly assigned ephemeral ID.
 */
function getResolvedAttribute(args: {
  attribute: Attribute;
  serviceInternalName: string;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
  accountAttrsByEquivalencyKey: Record<string, Attribute>;
  ephemeralIdAssigner: ReturnType<typeof createEphemeralAttributeIdAssigner>;
}): Attribute {
  const {
    attribute,
    serviceInternalName,
    accountCollisionMap,
    accountAttrsByEquivalencyKey,
    ephemeralIdAssigner,
  } = args;

  const equivalencyKey = getEquivalencyKeyForPrimitive({
    primitive: { attribute, serviceInternalName },
  });
  if (!accountCollisionMap.has(equivalencyKey)) {
    const existingEphemeralId = ephemeralIdAssigner.getEphemeralAttributeId(equivalencyKey);
    if (existingEphemeralId) {
      return {
        ...attribute,
        attribute_id: existingEphemeralId,
      };
    }
    return {
      ...attribute,
      attribute_id: ephemeralIdAssigner.assignEphemeralAttributeId(equivalencyKey),
    };
  }
  return accountAttrsByEquivalencyKey[equivalencyKey];
}


/**
 * Returns a copy of the Snippet with updated thresholds & data routes remapping any attribute
 * data in them to the service attributes defined in the target account (where the workflow lives).
 * Important assumptions and context:
 *  - all environments have the same Services
 *  - Attributes across environments do **not** have the same tokens across environments, but their
 *    selectors are consistent.
 *  - selectors are not unique within all services.
 */
function resolveServiceAttributes(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
  accountPrimitives: {
    services: ServiceObject[];
  };
}): SnippetVersion {
  const { snippet, accountCollisionMap, accountPrimitives } = args;

  // indices for constant-time lookup
  const snippetAttrs = getAttributesForServices(snippet.props.services);
  const snippetAttrsById = indexServiceAttributesById(snippetAttrs);
  const snippetServiceInternalNameByAttrId = indexServiceInternalNamesByAttributeId(
    snippet.props.services,
  );
  const accountAttrsByEquivalencyKey = indexAttributesByEquivalencyKey(
    accountPrimitives.services,
  );

  // ephemeral attribute ID handling
  const highestAccountAttributeId = getHighestAttributeId(accountPrimitives.services);
  const ephemeralIdAssigner = createEphemeralAttributeIdAssigner(highestAccountAttributeId);

  const resolvedThresholds = Object.values(snippet.data.thresholds).map(threshold => {
    const hasLeftAttr = Boolean(threshold.left.attributeId);
    const hasRightAttr = Boolean(threshold.right?.attributeId);

    if (!hasLeftAttr && !hasRightAttr) {
      return threshold;
    }

    const resolvedThreshold = structuredClone(threshold);

    if (hasLeftAttr) {
      const attrId = threshold.left.attributeId;
      const attribute = snippetAttrsById[attrId];
      const resolvedAttribute = getResolvedAttribute({
        attribute,
        serviceInternalName: snippetServiceInternalNameByAttrId[attrId],
        accountCollisionMap,
        accountAttrsByEquivalencyKey,
        ephemeralIdAssigner,
      });
      resolvedThreshold.left = {
        ...resolvedThreshold.left,
        attributeId: resolvedAttribute.attribute_id,
        attributeToken: resolvedAttribute.attribute_token,
      };
    }

    if (hasRightAttr) {
      const attrId = threshold.right.attributeId;
      const attribute = snippetAttrsById[attrId];
      const resolvedAttribute = getResolvedAttribute({
        attribute,
        serviceInternalName: snippetServiceInternalNameByAttrId[attrId],
        accountCollisionMap,
        accountAttrsByEquivalencyKey,
        ephemeralIdAssigner,
      });
      resolvedThreshold.right = {
        ...resolvedThreshold.right,
        attributeId: resolvedAttribute.attribute_id,
        attributeToken: resolvedAttribute.attribute_token,
      };
    }

    return resolvedThreshold;
  });

  const resolvedDataRoutes = Object.values(snippet.data.dataRoutes).map(dataRoute => {
    const hasAttr = Boolean(dataRoute.attribute.id);

    if (hasAttr) {
      const attrId = dataRoute.attribute.id;
      const attribute = snippetAttrsById[attrId];
      const resolvedAttribute = getResolvedAttribute({
        attribute,
        serviceInternalName: snippetServiceInternalNameByAttrId[attrId],
        accountCollisionMap,
        accountAttrsByEquivalencyKey,
        ephemeralIdAssigner,
      });
      return {
        ...dataRoute,
        attribute: {
          ...dataRoute.attribute,
          attributeName: resolvedAttribute.attribute_name,
          id: resolvedAttribute.attribute_id,
          token: resolvedAttribute.attribute_token,
        },
      };
    }

    return dataRoute;
  });

  // Resolve each service's attributes: for snippet-only attributes that were assigned
  // ephemeral IDs (when resolving thresholds/data routes)
  const resolvedServices = (snippet.props.services ?? []).map(service => {
    const attrs = service?.attributes ?? {};
    const internalName = service.internal_name;
    const resolvedAttrs = Object.fromEntries(
      Object.entries(attrs).map(([key, attr]) => {
        const equivalencyKey = getEquivalencyKeyForPrimitive({
          primitive: { attribute: attr, serviceInternalName: internalName },
        });
        const attrId = ephemeralIdAssigner.getEphemeralAttributeId(equivalencyKey) ?? Number(key);
        const resolved = { ...attr, attribute_id: attrId };
        return [attrId, resolved];
      }),
    ) as ServiceObject['attributes'];
    return { ...service, attributes: resolvedAttrs };
  });

  return createSnippetVersion({
    actions: snippet.data.actions,
    dataRoutes: indexDataRoutesByPublicId(resolvedDataRoutes),
    thresholds: indexThresholdsByPublicId(resolvedThresholds),
    outcomes: snippet.props.outcomes,
    services: resolvedServices,
    tags: snippet.props.tags,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions ?? [],
    metadata: snippet.metadata,
  });
}

/**
 * Returns a copy of the Snippet with updated service config identifiers in service action options.
 * ActionOption['serviceConfigId'] points to a specific config option tied to a service OR to a global config option
 * ActionOption['selectedValues'] is an array of serviceConfigPossibleValues associated with the above service config
 * ServiceConfigOptions + ServiceConfigPossibleValues are consistent across cells, but ids are not guaranteed to be the same (similar to Attributes)
 * Both use their slug as a consistent identifier, but serviceConfigOption slugs are only unique w/in their service (or globally)
 * + serviceConfigPossibleValue slugs are unique w/in their serviceConfigOption
 * serviceConfigOptionId will not *always* be populated. selectedValues is only populated for serviceConfigOptions w/a data type of 'multiselect'
 */
function resolveServiceConfigOptionIds(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
  accountPrimitives: {
    services: ServicesByName;
    globalServiceConfigOptions: ServiceConfigOption[];
  };
}): SnippetVersion {
  const {
    snippet,
    accountCollisionMap,
    accountPrimitives:
    {
      services: accountServices,
      globalServiceConfigOptions: accountGlobalServiceConfigOptions,
    },
  } = args;
  // service actions in snippets
  const snippetServicesByName = indexServicesByInternalName(snippet.props.services);
  const snippetServiceConfigOptionsByEquivalencyKey = indexServiceConfigOptionsByEquivalencyKey({
    services: snippetServicesByName,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions,
  });
  const accountServiceConfigOptionsByEquivalencyKey = indexServiceConfigOptionsByEquivalencyKey({
    services: accountServices,
    globalServiceConfigOptions: accountGlobalServiceConfigOptions,
  });

  const resolvedActions = Object.values(snippet.data.actions).map(action => {
    // we can safely skip list service actions as they do not have service configs
    if (!isActionOfType(action, ActionType.SERVICE) || isListAction(action)) {
      return action;
    }
    // we only need to resolve service actions w/options
    if (Object.keys(action.options).length === 0) {
      return action;
    }
    const resolvedAction = structuredClone(action);
    for (const [slug, option] of Object.entries(resolvedAction.options)) {
      // only match options w/service config option id (legacy options do not have SCO id)
      if (!option.serviceConfigOptionId) {
        /* eslint-disable-next-line no-continue */
        continue;
      }
      const equivalencyKey = getEquivalencyKeyForServiceConfigOption(
        extractIdentifiersFromServiceActionOption({
          action: resolvedAction,
          optionSlug: slug,
          globalServiceConfigOptions: snippet.props.globalServiceConfigOptions,
        }));

      if (!accountCollisionMap.has(equivalencyKey)) {
        throw new Error(
          `resolveServiceConfigOptionIds: Missing service config in target account for ${equivalencyKey}`,
        );
      }
      const snippetServiceConfigOption = snippetServiceConfigOptionsByEquivalencyKey.get(
        equivalencyKey)!;
      const accountServiceConfigOption = accountServiceConfigOptionsByEquivalencyKey.get(
        equivalencyKey)!;
      // IF option has selectedValues, resolve snippet value IDs to account IDs before updating service config option id.
      if (option.selectedValues && option.selectedValues.length > 0) {
        const optionSlugArray = convertActionOptionSelectedValueIdsToSlugs(
          option,
          snippetServiceConfigOption);
        const accountPossibleValueSlugMap = indexServiceConfigPossibleValuesBySlug(
          accountServiceConfigOption.possibleValues);
        option.selectedValues = optionSlugArray.map(possibleValueSlug => {
          const possibleValue = accountPossibleValueSlugMap[possibleValueSlug];
          if (!possibleValue) {
            throw new Error(
              `resolveServiceConfigOptionIds: possible value slug "${possibleValueSlug}" missing from account config for ${equivalencyKey}`,
            );
          }
          return possibleValue.serviceConfigPossibleValueId;
        });
      }
      option.serviceConfigOptionId = accountServiceConfigOption.serviceConfigOptionId;
    }
    return resolvedAction;
  });
  return createSnippetVersion({
    actions: indexActionsByPublicId(resolvedActions),
    dataRoutes: snippet.data.dataRoutes,
    thresholds: snippet.data.thresholds,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    tags: snippet.props.tags,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions ?? [],
    metadata: snippet.metadata,
  });
}
/**
 * Returns a copy of the Snippet with updated published attribute tokens in both props and actions.
 * Resolving means that if a published attribute token is not found in the account, a temporary token is generated,
 * and the PA prop & details within an action are updated to use the temporary token.
 */
function resolvePublishedAttributes(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
}): SnippetVersion {
  const { snippet, accountCollisionMap } = args;

  const resolvedSnippetPublishedAttributeProps = snippet
    .props.publishedAttributes
    .map(snippetPublishedAttribute => {
      const snippetPublishedAttributeEquivalencyKey = getEquivalencyKeyForPrimitive({
        primitive: snippetPublishedAttribute,
      });

      if (!accountCollisionMap.has(snippetPublishedAttributeEquivalencyKey)) {
        /**
         * the PA token is set by the backend, but the FE needs to have a consistent token
         * across PA props and its Actions
         */
        const temporaryPublishedAttributeToken = generateTemporaryPublishedAttributeToken();
        return {
          ...snippetPublishedAttribute,
          attributeToken: temporaryPublishedAttributeToken,
        };
      }

      return snippetPublishedAttribute;
    });

  const resolvedSnippetPublishedAttributesByName = indexPublishedAttributesByName(
    resolvedSnippetPublishedAttributeProps,
  );

  const resolvedActions = Object.values(snippet.data.actions).map(snippetAction => {
    // we only need to resolve PA snippet actions
    if (!isPublishedAttribute(snippetAction)) {
      return snippetAction;
    }

    const snippetAttributeAction = getPublishedDetails(snippetAction)?.attributeName;
    const resolvedPublishedAttributePropForAction = (
      resolvedSnippetPublishedAttributesByName[snippetAttributeAction]
    );
    if (!resolvedPublishedAttributePropForAction) {
      return snippetAction;
    }

    const resolvedAttributeToken = resolvedPublishedAttributePropForAction.attributeToken;

    if (isActionOfType(snippetAction, ActionType.INPUT)) {
      return {
        ...snippetAction,
        input: {
          ...snippetAction.input,
          publishedAttributeToken: resolvedAttributeToken,
          publishedDetails: {
            ...snippetAction.input.publishedDetails,
            attributeToken: resolvedAttributeToken,
          },
        },
      };
    }
    if (isActionOfType(snippetAction, ActionType.OUTPUT)) {
      return {
        ...snippetAction,
        output: {
          ...snippetAction.output,
          publishedAttributeToken: resolvedAttributeToken,
          publishedDetails: {
            ...snippetAction.output.publishedDetails,
            attributeToken: resolvedAttributeToken,
          },
        },
      };
    }

    // should not get here as all non-input/output actions are covered by early return
    return snippetAction;
  });


  return createSnippetVersion({
    actions: indexActionsByPublicId(resolvedActions),
    thresholds: snippet.data.thresholds,
    dataRoutes: snippet.data.dataRoutes,
    tags: snippet.props.tags,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    publishedAttributes: resolvedSnippetPublishedAttributeProps,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions ?? [],
    metadata: snippet.metadata,
  });
}


/**
 * returns a copy of the snippet with its ReasonCodeGroups (RCG) properties updated such that:
 * - if a ReasonCodeGroup in the snippet collides (is equivalent) with one from the account, the account's RCG is used instead.
 * - any reference to a ReasonCodeGroup in a threshold (right.reasonCodeGroup) is also updated to the account's if a collision exists.
 * - otherwise, if there is no collision, the snippet RCG is kept, but its reasonCodes are remapped to account reasonCodes via their unique service/code keys.
 *
 * this function ensures that RCGs from account data take precedence and are reused in the snippet wherever possible.
 */
function resolveReasonCodeGroups(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
  accountPrimitives: {
    reasonCodeGroups: ReasonCodeGroup[];
    reasonCodes: ReasonCode[];
  };
}): SnippetVersion {
  const { snippet, accountPrimitives, accountCollisionMap } = args;

  // pre-compute indices as they are used in more than one loop
  const accountRCGsByName = indexReasonCodeGroupsByName(accountPrimitives.reasonCodeGroups);
  const accountRCsByServiceCodeKey = indexReasonCodesByServiceCodeKey(
    accountPrimitives.reasonCodes,
  );

  // the equivalencyKey for RCGs has a nested loop so they are computed upfront
  const equivalencyKeysForSnippetRCGByName = indexPrimitiveEquivalencyKeysByIdentifier({
    identifierExtractor: reasonCodeGroup => reasonCodeGroup.name,
    primitives: snippet.props.reasonCodeGroups,
  });

  /**
   * RCGs resolved to either:
   * - use the RCG from the account (if there's a collision),
   * - or update the RCs inside of the snippet RCG to match the account RCs.
   *
   * note: if a RCG does not have a collision (is ephemeral), an `id` must be given to it
   * that is the next available one
   */
  let ephemeralRCGCount = 0;
  const highestReasonCodeGroupId = getHighestReasonCodeGroupId(accountPrimitives.reasonCodeGroups);

  const resolvedReasonCodeGroups = snippet.props.reasonCodeGroups.map(snippetRCG => {
    const snippetRCGEquivalencyKey = equivalencyKeysForSnippetRCGByName[snippetRCG.name];
    const hasCollision = accountCollisionMap.has(snippetRCGEquivalencyKey);

    if (hasCollision) {
      return accountRCGsByName[snippetRCG.name];
    }

    // keep a separate counter because the accountPrimitives.reasonCodeGroups length wont change until insertion
    ephemeralRCGCount += 1;
    return {
      ...snippetRCG,
      id: highestReasonCodeGroupId + ephemeralRCGCount,
      reasonCodes: snippetRCG.reasonCodes.flatMap(snippetReasonCode => {
        // ignore numeric reason codes
        if (typeof snippetReasonCode === 'number') {
          return [];
        }
        const reasonCodeServiceCodeKey = createReasonCodeServiceCodeKey(snippetReasonCode);
        const accountReasonCode = accountRCsByServiceCodeKey[reasonCodeServiceCodeKey];
        // the accountReasonCode should always be found, but providing a fallback just in case
        return accountReasonCode ?? snippetReasonCode;
      }),
    };
  });


  // index the resolved RCGs so we can use them for thresholds
  const snippetResolvedRCGsByName = indexReasonCodeGroupsByName(resolvedReasonCodeGroups);

  /**
   * resolved the threshold RCGs to either:
   * - use the RCG in the account
   * - use the resolved snippet RCG from the step above
   */
  const resolvedThresholds = Object.values(snippet.data.thresholds).map(snippetThreshold => {
    // do not resolve thresholds that have no RCGs in them
    if (!snippetThreshold.right?.reasonCodeGroup) {
      return snippetThreshold;
    }

    const thresholdRCG = snippetThreshold.right.reasonCodeGroup;
    const thresholdRCGEquivalencyKey = equivalencyKeysForSnippetRCGByName[thresholdRCG.name];
    const hasCollision = accountCollisionMap.has(thresholdRCGEquivalencyKey);

    const rcgToUse = hasCollision
      ? accountRCGsByName[thresholdRCG.name]
      : snippetResolvedRCGsByName[thresholdRCG.name];

    return {
      ...snippetThreshold,
      right: {
        ...snippetThreshold.right,
        reasonCodeGroup: rcgToUse,
        reasonCodeGroupId: rcgToUse.id,
      },
    };
  });

  return createSnippetVersion({
    actions: snippet.data.actions,
    dataRoutes: snippet.data.dataRoutes,
    thresholds: indexThresholdsByPublicId(resolvedThresholds),
    outcomes: snippet.props.outcomes,
    publishedAttributes: snippet.props.publishedAttributes,
    services: snippet.props.services,
    tags: snippet.props.tags,
    reasonCodeGroups: resolvedReasonCodeGroups,
    lists: snippet.props.lists,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions,
    metadata: snippet.metadata,
  });
}


/**
 * returns a copy of the snippet with its list props + actions updated such that:
 * - If a list is not equivalent with one from the account, a temporary list token is generated for the prop
 *  and that token is used as the value for the `customer_list_id` option
 * This prevents id collisions if c/p-ing between cells and ensures that we are using a temporary value
 */
function resolveLists(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
}): SnippetVersion {
  const { snippet, accountCollisionMap } = args;

  const resolvedSnippetListProps = snippet.props.lists.map(snippetList => {
    const snippetListEquivalencyKey = getEquivalencyKeyForPrimitive({ primitive: snippetList });
    if (!accountCollisionMap.has(snippetListEquivalencyKey)) {
      // lists have a token, but use customer_list_id as the identifier
      // to prevent accidental collisions, we are using the temporary token as the id
      const temporaryListToken = generateTemporaryListToken();
      return {
        ...snippetList,
        list_token: temporaryListToken,
        customer_list_id: temporaryListToken,
      };
    }
    return snippetList;
  });

  const resolvedSnippetListsByName = indexListsByName(resolvedSnippetListProps);

  const resolvedActions = Object.values(snippet.data.actions).map(snippetAction => {
    if (!isListAction(snippetAction)) {
      return snippetAction;
    }

    const snippetListActionName = getActionName(snippetAction);
    const resolvedListPropForAction = resolvedSnippetListsByName[snippetListActionName];

    if (!resolvedListPropForAction) {
      return snippetAction;
    }

    const resolvedListIdentifier = resolvedListPropForAction.customer_list_id;

    return {
      ...snippetAction,
      options: {
        ...snippetAction.options,
        [CUSTOMER_LIST_ID_OPTION_KEY]: {
          ...snippetAction.options[CUSTOMER_LIST_ID_OPTION_KEY],
          value: resolvedListIdentifier,
        },
      },
    };
  });

  return createSnippetVersion({
    actions: indexActionsByPublicId(resolvedActions),
    thresholds: snippet.data.thresholds,
    dataRoutes: snippet.data.dataRoutes,
    tags: snippet.props.tags,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: resolvedSnippetListProps,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions ?? [],
    metadata: snippet.metadata,
  });
}


/**
 * Returns a copy of the Snippet with resolved precedence indexed actions (Outcomes and Workflow Routers).
 * The precedence index of these actions are resolved to be incrementally and sequentially higher than
 * the one of the workflow, while leaving no gaps in between them.
 * For example:
 *
 * snippet Action indices:
 *    - A(1)
 *    - B(2) (has collision against workflow)
 *    - C(3)
 *
 * workflow max index: 2
 * result:
 *    - A(3) -> next number after workflow max of 2
 *    - B(2) -> has collision, index is not updated
 *    - C(4) -> next number after snippet A
 *
 * note: B is not modified because it will not be inserted by the algorithm later on. although this does create
 * hidden coupling between the contextualization pipeline and the insertion algorithm, it simplifies it by not
 * putting the responsibility of adjusting the indices on it.
 */
function resolvePrecedenceIndexedActions(args: {
  snippet: SnippetVersion;
  accountCollisionMap: Map<EquivalencyKey, Set<string>>;
  workflowPrimitives: {
    actions: Record<Action['publicId'], Action>;
  };
}): SnippetVersion {
  const { snippet, accountCollisionMap, workflowPrimitives } = args;


  const workflowPrecedenceIndexedActions = getPrecedenceIndexedActions(workflowPrimitives.actions);

  // if there are no precedence indexed actions in the WF, there is nothing to resolve in the snippet
  if (workflowPrecedenceIndexedActions.length === 0) {
    return snippet;
  }

  const highestWorkflowActionPrecedence = findHighestExistingActionPrecedence(
    workflowPrecedenceIndexedActions,
  );

  const snippetNonPrecedenceIndexedActions = getNonPrecedenceIndexedActions(snippet.data.actions);
  const snippetPrecedenceIndexedActionsASC = sortActionsByPrecedenceIndex(
    getPrecedenceIndexedActions(snippet.data.actions),
  );


  let skippedActionCounter = 0;
  const resolvedSnippetPrecedenceIndexedActions = snippetPrecedenceIndexedActionsASC
    .map(snippetPrecedenceIndexedAction => {
      // do not modify the default action as it'll be inserted by the insertion algorithm
      if (isDefaultPrecedenceIndexedAction(snippetPrecedenceIndexedAction)) {
        skippedActionCounter += 1;
        return snippetPrecedenceIndexedAction;
      }


      const snippetPIActionEquivalencyKey = getEquivalencyKeyForPrimitive({
        primitive: snippetPrecedenceIndexedAction,
      });

      // do not modify a colliding action, but track that we've skipped it
      const hasCollision = accountCollisionMap.has(snippetPIActionEquivalencyKey);
      if (hasCollision) {
        skippedActionCounter += 1;
        return snippetPrecedenceIndexedAction;
      }

      const snippetActionPrecedenceIndex = getActionPrecedenceIndex(snippetPrecedenceIndexedAction);
      const nextPrecedenceIndex = (
        /**
         * use `highestWorkflowActionPrecedence` as the offset for the incoming `snippetActionPrecedenceIndex`,
         * but subtract the count of skipped actions to remove any gaps between indices
         */
        highestWorkflowActionPrecedence + snippetActionPrecedenceIndex - skippedActionCounter
      );

      return setActionPrecedenceIndex(snippetPrecedenceIndexedAction, nextPrecedenceIndex);
    });


  const resolvedActions = [
    ...snippetNonPrecedenceIndexedActions,
    ...resolvedSnippetPrecedenceIndexedActions,
  ];


  return createSnippetVersion({
    actions: indexActionsByPublicId(resolvedActions),
    dataRoutes: snippet.data.dataRoutes,
    thresholds: snippet.data.thresholds,
    outcomes: snippet.props.outcomes,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    services: snippet.props.services,
    tags: snippet.props.tags,
    lists: snippet.props.lists,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions ?? [],
    metadata: snippet.metadata,
  });
}
